import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {containersSelector, useGetAllContainersQuery} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {ContainerDataTable} from "../../components/data-table/ContainerDataTable/ContainerDataTable";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useNavigate} from "react-router-dom";

export type ContainerDataTablePageProps = {}

export const ContainerDataTablePage = (props: ContainerDataTablePageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate();

  const [access, setAccess] = useState(false);

  const selectContainers = useMemo(() => {
    return containersSelector()
  }, []);

  const {containers, isLoading, error, refetch} = useGetAllContainersQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      containers: selectContainers(result, {regionId}),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  return (
    <ProtectedPage permissions={["container_view"]} isLoading={isLoading} error={error} onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.container.title", "Containers", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["container_create"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/containers/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <ContainerDataTable
                  withBorder
                  records={containers}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
